import React, { useEffect, useState, useRef } from "react";
import Images from "../../assets/images/Images";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import {
    customerData,
    executiveData as executiveDataApi,
    ordersData,
    productData,
    categoryData,
    addCustomer,
    updateCustomer,
    getAllQuantityTypes,
    deleteQuantityType
} from "../common/globalapi/Api";
import moment from "moment";
import CreateQuantityType from "../product/CreateQuantityType";
import { handlePosPrint, PRINTER_PROFILES } from "../../utils/posPrint";

// Custom Option to render Edit and Delete buttons inline
const CustomOption = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex justify-content-between align-items-center w-100">
                <span className="text-truncate">{props.data.label}</span>
                {props.data.id && (
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0 text-primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (props.selectProps.onEditItem) props.selectProps.onEditItem(props.data);
                            }}
                            title="Edit"
                        >
                            <i className="bi bi-pencil"></i>
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0 text-danger"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (props.selectProps.onDeleteItem) props.selectProps.onDeleteItem(props.data);
                            }}
                            title="Delete"
                        >
                            <i className="bi bi-trash"></i>
                        </button>
                    </div>
                )}
            </div>
        </components.Option>
    );
};




const NewSales = ({ activeTab }) => {
    const { state } = useLocation();
    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        getValues,
        reset,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            invoiceNo: "",
            orderProducts: [
                {
                    product: "",
                    quantity: 1,
                    quantityType: "",
                    otherQuantityType: "",
                    unitPrice: "",
                    price: "",
                    orderAdd: "",
                    gstPercentage: "",
                    mrpPrice: "",
                    hsnCode: "",
                    salesDate: moment().format("YYYY-MM-DD"),
                },
            ],
            salesDate: moment().format("YYYY-MM-DD"), // today
            receivedAmount: "",
            balanceAmount: 0,
            totalInvoiceAmount: state?.totalInvoiceAmount || 0,
            transactionMode: "Online",
            transactionStatus: "Completed",
            deliveryStatus: "Delivered",
            selectFormat: "a5",
            customerLevel: "Retailer",
        },
    });
    const { fields: orderProductFields, append, remove, replace, update } = useFieldArray({
        control,
        name: "orderProducts",
    });

    // --- Watch variables (declared early to avoid "access before initialization" errors) ---
    const watchedOrderProducts = useWatch({ control, name: "orderProducts", defaultValue: [] });
    const customersLevels = watch("customerLevel") || "Retailer";
    const selectedName = watch("customerName");
    const { customerName, contact } = watch();
    const currentReceivedAmt = Number(useWatch({ control, name: "receivedAmount" }) || 0);
    const totalAmount = Number(useWatch({ control, name: "totalInvoiceAmount" }) || 0);
    const customerLevelBase = Number(useWatch({ control, name: "customerLevel" }) || 0);
    const discount = Number(useWatch({ control, name: "discountPrice" }) || 0);
    const gstPercent = Number(useWatch({ control, name: "gstPercentage" }) || 0);

    // --- Derived Calculations ---
    const userId = sessionStorage.getItem("userId");
    const companyDetails = (() => {
        try {
            return JSON.parse(localStorage.getItem("companyDetails") || "{}");
        } catch (e) {
            return {};
        }
    })();
    const registerId = state?.registerId || companyDetails?.id || userId || 1;

    const queryClient = useQueryClient();
    const [editingQuantityType, setEditingQuantityType] = useState(null);

    // Fetch Quantity Types
    const {
        data: quantityTypesData = [],
        isLoading: quantityTypesLoading,
    } = useQuery({
        queryKey: ["quantityTypes", userId],
        queryFn: async () => {
            if (!userId) return [];
            const resp = await getAllQuantityTypes(userId);
            return resp.data || [];
        },
        enabled: !!userId,
        refetchOnWindowFocus: false,
    });

    // DELETE API for Quantity Type
    const deleteQuantityMutation = useMutation({
        mutationFn: async (id) => {
            const response = await deleteQuantityType(userId, id);
            return response.data;
        },
        onSuccess: () => {
            toast.success("Quantity Type deleted successfully!");
            queryClient.invalidateQueries(["quantityTypes", userId]);
        },
        onError: (error) => {
            toast.error(error?.response?.data || "Failed to delete Quantity Type.");
        }
    });

    const handleDeleteQuantityType = (data) => {
        if (window.confirm(`Are you sure you want to delete '${data.label}'?`)) {
            deleteQuantityMutation.mutate(data.id);
        }
    };

    const handleEditQuantityType = (data) => {
        setEditingQuantityType(data.originalData);
        const modalBtn = document.getElementById("hiddenCreateQtyModalBtn");
        if (modalBtn) modalBtn.click();
    };

    // --- API Queries (declared early to be used in effects) ---
    const {
        data: productDetails,
        isLoading: purchaseLoading,
    } = useQuery({
        queryKey: ["productCatalog", registerId],
        queryFn: () => productData(registerId, true),
        enabled: !!registerId,
        refetchOnWindowFocus: false,
    });
    const products = productDetails?.data || [];

    const {
        data: executiveData,
        isLoading: executiveLoading,
        refetch: executiveRefetch,
    } = useQuery({
        queryKey: ["executive"],
        queryFn: executiveDataApi,
        refetchOnWindowFocus: false,
    });
    const {
        data: customersData,
        isLoading: customersLoading,
    } = useQuery({
        queryKey: ["customersList", registerId],
        queryFn: () => customerData(registerId),
        enabled: !!registerId,
        refetchOnWindowFocus: false,
    });

    const customers = customersData?.data || [];
    const customerOptions = customers.map((c) => {
        const contact = c.contact || c.contactNumber || "";
        return {
            label: `${c.customerName}${contact ? ` : ${contact}` : ""}`,
            value: c.customerName,
            customer: c,
        };
    });

    const {
        data: categoriesData,
        isLoading: categoriesLoading,
    } = useQuery({
        queryKey: ["categoriesList", registerId],
        queryFn: () => categoryData(registerId),
        enabled: !!registerId,
        refetchOnWindowFocus: false,
    });

    const dynamicCategories = categoriesData?.data || [];
    const categoriesList = ["All", ...(Array.isArray(dynamicCategories) ? dynamicCategories : [])];

    const [selectedCategory, setSelectedCategory] = useState("All");
    const [searchTermFast, setSearchTermFast] = useState("");
    const [isCountModalOpen, setIsCountModalOpen] = useState(false);
    const [tempProduct, setTempProduct] = useState(null);
    const [tempCount, setTempCount] = useState(1);

    const navigate = useNavigate();

    // --- Auto-calculate price for each product row ---
    // (Removed useEffect to prevent infinite loops and re-renders; price is derived on calculation and submit)

    // --- Dynamic Customer Level Based Unit Price mapping ---
    const watchedProductNames = (watchedOrderProducts || []).map(p => p.product).join(',');
    useEffect(() => {
        (watchedOrderProducts || []).forEach((item, idx) => {
            if (!item.product) return;

            // Try to find the product in the local products list (inventory)
            // Note: 'products' is defined later via useQuery, so we must handle it being undefined
            const selectedProduct = (products || [])?.find((p) =>
                p?.productName === item.product ||
                p?.productNameEnglish === item.product ||
                p?.product === item.product
            );

            if (selectedProduct) {
                const levelPriceMap = {
                    "MRP": selectedProduct?.highLevelCustomerPrice || selectedProduct?.highLevelCustomer,
                    "Retailer": selectedProduct?.midLevelCustomerPrice || selectedProduct?.midLevelCustomer,
                    "Wholesale": selectedProduct?.lowLevelCustomerPrice || selectedProduct?.lowLevelCustomer,
                };
                const mappedPrice = levelPriceMap[customersLevels] || selectedProduct?.highLevelCustomerPrice || selectedProduct?.highLevelCustomer || 0;

                if (item.unitPrice !== mappedPrice) {
                    setValue(`orderProducts.${idx}.unitPrice`, mappedPrice);
                }
            }
        });
    }, [customersLevels, products, watchedProductNames, setValue]);

    // GET API START FOR ORDERS
    const {
        data: orderData,
        isLoading: orderLoading,
        refetch: orderRefetch,
    } = useQuery({
        queryKey: ["order", contact],
        queryFn: () => ordersData(contact),
        enabled: !!contact && activeTab !== "fast",
        refetchOnWindowFocus: false,
    });

    let getByNumber = (orderData && orderData?.data) || [];
    const lastOrder = getByNumber[getByNumber?.length - 1];
    const payments = (lastOrder && lastOrder?.payments) || [];
    const existingBalance = payments?.length > 0 ? Number(payments[payments?.length - 1].balanceAmount || 0) : 0;

    const [paymentList, setPaymentList] = useState(state?.payments || []);
    const cumulativeReceived = paymentList?.reduce((sum, p) => sum + Number(p.receivedAmount || 0), 0);

    // --- Optimized Calculations with useMemo ---
    const isEditMode = React.useMemo(() => Boolean(state?.orderId || state?.id), [state]);

    const productsCalculation = React.useMemo(() => {
        return (Array.isArray(watchedOrderProducts) ? watchedOrderProducts.filter(item => item?.product).map((item) => {
            const quantity = Number(item?.quantity || 0);
            const unitPrice = Number(item?.unitPrice || 0);
            const gstPercentLevel = Number(item?.gstPercentage || 0);

            const baseAmount = Number(quantity * unitPrice);
            const gstAmount = Number((baseAmount * gstPercentLevel) / 100);
            const totalRowAmount = Number(baseAmount + gstAmount);

            return {
                baseAmount: Number(baseAmount.toFixed(2)),
                gstAmount: Number(gstAmount.toFixed(2)),
                totalAmount: Number(totalRowAmount.toFixed(2)),
            };
        }) : []);
    }, [watchedOrderProducts]);

    const totalBeforeDiscount = React.useMemo(() =>
        Number(productsCalculation.reduce((sum, p) => sum + (Number(p?.totalAmount) || 0), 0).toFixed(2)),
        [productsCalculation]
    );

    const memoTotalInvoiceAmount = React.useMemo(() => {
        let total = Number(totalBeforeDiscount) - (Number(discount) || 0);
        if (!isEditMode && Number(existingBalance) > 0) {
            total += Number(existingBalance);
        }
        return Math.round(Number(total || 0));
    }, [totalBeforeDiscount, discount, isEditMode, existingBalance]);

    const memoBalanceAmount = React.useMemo(() => {
        const total = Number(totalAmount) || 0;
        const cumulative = Number(cumulativeReceived) || 0;
        const current = Number(currentReceivedAmt) || 0;
        const balance = total - cumulative - current;
        return Number(Number(balance || 0).toFixed(2));
    }, [totalAmount, cumulativeReceived, currentReceivedAmt]);

    // Update form values based on memoized calculations
    useEffect(() => {
        setValue("totalInvoiceAmount", memoTotalInvoiceAmount);
    }, [memoTotalInvoiceAmount, setValue]);

    useEffect(() => {
        setValue("balanceAmount", memoBalanceAmount);
    }, [memoBalanceAmount, setValue]);

    const totalWithPrevious = Number(totalAmount) + Number(existingBalance);
    const currentBalance = totalWithPrevious - cumulativeReceived;

    // Auto update transaction status
    // useEffect(() => {
    //     if (totalWithPrevious > 0 && currentBalance <= 0) {
    //         setValue("transactionStatus", "Completed");
    //     } else {
    //         setValue("transactionStatus", "Pending");
    //     }
    // }, [currentBalance, totalWithPrevious, setValue]);


    // POST API START
    const { mutate, isPending: postLoading } = useMutation({
        mutationFn: async (postData) => {
            const response = await axios.post(
                `${process.env.REACT_APP_HOST}/api/orders/add`,
                postData,
                { headers: { "Content-Type": "application/json" } }
            );
            return response.data;
        },
        onSuccess: () => {
            toast.success("Order Added Successfully!");
            setTimeout(() => navigate("/viewOrder"), 2000);
        },
        onError: (error) => {
            if (axios.isAxiosError(error) && error.response?.status === 400) {
                const message = error.response?.data?.message || "Bad Request: Please check your input and try again.";
                toast.error(message);
            } else {
                toast.error("Failed to add Orders. Please try again.");
            }
        },
    });
    // UPDATE API
    const { mutate: updateApi, isPending: putLoading } = useMutation({
        mutationFn: async (postData) => {
            const response = await axios.put(
                `${process.env.REACT_APP_HOST}/api/orders/editOrder/${registerId}/${state?.orderId || state?.id}`,
                postData,
                { headers: { "Content-Type": "application/json" } }
            );
            return response.data;
        },
        onSuccess: () => {
            toast.success("Order Updated Successfully!");
            setTimeout(() => navigate("/viewOrder"), 2000);
        },
        onError: (error) => {
            console.error("Mutation failed:", error);
            toast.error("Failed to update the Orders. Please try again.");
        },
    });

    // REPEAT API START
    const { mutate: repeatMutate, isPending: repeatLoading } = useMutation({
        mutationFn: async (postData) => {
            const response = await axios.post(
                `${process.env.REACT_APP_HOST}/api/orders/add`,
                postData,
                { headers: { "Content-Type": "application/json" } }
            );
            return response.data;
        },
        onSuccess: () => {
            toast.success("Order Added Successfully!");
            setTimeout(() => navigate("/viewOrder"), 2000);
        },
        onError: (error) => {
            console.error("Mutation failed:", error);
            toast.error("Failed to add Orders. Please try again.");
        },
    });

    // CUSTOMER SYNC MUTATIONS
    const { mutate: addCustomerMutate } = useMutation({
        mutationFn: addCustomer,
        onSuccess: () => {
            console.log("Customer Added via Sync");
        },
        onError: (error) => {
            console.error("Failed to sync add customer:", error);
        },
    });

    const { mutate: updateCustomerMutate } = useMutation({
        mutationFn: ({ registerId, customerId, data }) => updateCustomer(registerId, customerId, data),
        onSuccess: () => {
            console.log("Customer Updated via Sync");
        },
        onError: (error) => {
            console.error("Failed to sync update customer:", error);
        },
    });

    // SETVALUE FOR STATE VALUE

    console.log("state", state);

    useEffect(() => {
        if (state) {
            setValue("id", state?.id || 0);
            setValue("orderId", state?.orderId || "");
            setValue("customerName", state?.customerName || "");
            setValue("customerAddress", state?.customerAddress || "");
            // API returns contactNumber, form uses contact
            setValue("contact", state?.contact || state?.contactNumber || null);
            setValue("pincode", state?.pincode || "");
            setValue(
                "salesDate",
                state?.salesDate ? moment(state?.salesDate).format("YYYY-MM-DD") : null
            );

            setValue("gstNumber", state?.gstNumber || "");
            setValue("customerLevel", state?.customerLevel || "");
            setValue("lineroute", state?.lineroute || "");
            setValue("routeName", state?.routeName || "");
            setValue("state", state?.state || "");
            setValue("executiveName", state?.executiveName || "");
            setValue("transactionMode", state?.transactionMode || "");
            setValue("transactionStatus", state?.transactionStatus || "");
            setValue("deliveryStatus", state?.deliveryStatus || "");
            setValue("gstPercentage", state?.gstPercentage || "");
            setValue("discountPrice", state?.discountPrice || "");
            // API returns totalAmount, form uses totalInvoiceAmount
            setValue("totalInvoiceAmount", state?.totalInvoiceAmount || state?.totalAmount || "");
            setValue("selectFormat", state?.selectFormat || "");
            // API returns vehicleNumber (capital N), form uses vehiclenumber
            setValue("vehiclenumber", state?.vehiclenumber || state?.vehicleNumber || "");
            setValue("invoiceNo", state?.invoiceNo || "");
            // API returns receivedAmount - must be set before balanceAmount effect runs
            setValue("receivedAmount", state?.receivedAmount ?? "");
            setValue("balanceAmount", state?.balanceAmount ?? 0);

            // Mapping orderProducts from state (items or orderProducts)
            const productsFromState = state?.items || state?.orderProducts || [];
            if (productsFromState.length > 0) {
                replace(
                    productsFromState.map((item) => ({
                        product: item?.productName || item?.product || null,
                        quantity: item?.quantity || null,
                        unitPrice: item?.unitPrice || null,
                        discountPrice: item?.discountPrice || null,
                        quantityType: item?.quantityType || null,
                        price: item?.price || null,
                        orderAdd: item?.orderAdd || null,
                        salesDate: item?.salesDate || null,
                        hsnCode: item?.hsnCode || null,
                        mrpPrice: item?.mrpPrice || item?.mrp || null,
                        gstPercentage: item?.gstPercentage ?? item?.gstPercent ?? null,
                    }))
                );
            }
        }
    }, [state, setValue, replace]);

    // FORM SUBMIT
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [orderFormData, setOrderFormData] = useState(null);

    const onSubmit = (data) => {
        const newReceived = cumulativeReceived + currentReceivedAmt;
        const newBalance = totalAmount - newReceived;
        const paymentStatus = newBalance <= 0 ? "Completed" : "Pending";

        const newPayment = {
            totalAmount,
            receivedAmount: currentReceivedAmt,
            balanceAmount: newBalance,
            paymentDate: new Date().toISOString().split("T")[0],
            status: paymentStatus,
        };

        // const updatedList = [...paymentList, newPayment];
        // setPaymentList(updatedList);

        let updatedList = [];
        if (paymentList?.length > 0) {
            // Replace last payment row
            updatedList = [...paymentList];
            updatedList[updatedList.length - 1] = newPayment;
        } else {
            // First time payment
            updatedList = [newPayment];
        }

        setPaymentList(updatedList);

        // Reset form fields only for non-fast flow. For fast billing we open popup and keep form intact.
        if (activeTab !== "fast") {
            reset({
                receivedAmount: "",
                balanceAmount: 0,
                totalInvoiceAmount: totalWithPrevious,
                transactionStatus: paymentStatus,
            });
        }

        // CUSTOMER SYNC LOGIC
        const existingCustomer = customers.find(c =>
            (c.customerName || "").toString().toLowerCase() === (data?.customerName || "").toString().toLowerCase()
        );

        const customerSyncData = {
            registerId: registerId,
            customerName: data?.customerName || "",
            contact: data?.contact || "",
            gstNumber: data?.gstNumber || "",
        };

        if (existingCustomer) {
            // Update existing customer
            updateCustomerMutate({
                registerId: registerId,
                customerId: existingCustomer.id,
                data: customerSyncData
            });
        } else if (data?.customerName) {
            // Add new customer
            addCustomerMutate(customerSyncData);
        }

        const formData = {
            registerId: registerId,
            customerName: data?.customerName || "",
            contactNumber: data?.contact || "",
            salesDate: data?.salesDate || moment().format("YYYY-MM-DD"),
            gstNumber: data?.gstNumber || "",
            totalAmount: Number(data?.totalInvoiceAmount || 0),
            receivedAmount: Number(currentReceivedAmt || 0),
            balanceAmount: Number((data?.totalInvoiceAmount || 0) - (currentReceivedAmt || 0)),
            paymentDate: data?.salesDate || moment().format("YYYY-MM-DD"),
            executiveName: data?.executiveName || "",
            vehicleNumber: data?.vehiclenumber || "",
            transactionMode: data?.transactionMode || "",
            invoiceNo: data?.invoiceNo || "",
            transactionStatus: paymentStatus,
            deliveryStatus: data?.deliveryStatus || "Processing",
            items: (Array.isArray(watchedOrderProducts) ? watchedOrderProducts : [])
                .filter(p => p.product && Number(p.quantity) >= 1)
                .map((p) => {
                    const quantity = Number(p.quantity || 0);
                    const unitPrice = Number(p.unitPrice || 0);
                    const gstPercentLevel = Number(p.gstPercentage || 0);
                    const subtotal = quantity * unitPrice;
                    const gstAmount = (subtotal * gstPercentLevel) / 100;
                    const rowTotalPrice = Number((subtotal + gstAmount).toFixed(2));

                    return {
                        productName: p.product || p.productName || p.productNameEnglish || "",
                        hsnCode: p.hsnCode || "",
                        quantity: quantity,
                        quantityType: p.quantityType || "",
                        unitPrice: unitPrice,
                        gstPercent: gstPercentLevel,
                        price: rowTotalPrice,
                        mrp: Number(p.mrpPrice || 0),
                    };
                }),
            discount: Number(data?.discountPrice || 0)
        };

        // Show BOS print popup before saving for both Fast and Manual Billing.
        console.log("onSubmit prepared formData:", formData);
        setOrderFormData(formData);

        // If the submit action was triggered by the direct save button flag, execute mutate right away
        if (submitActionTypeRef.current === "save") {
            if (isEditMode) updateApi(formData);
            else mutate(formData);
        } else if (submitActionTypeRef.current === "print") {
            setIsPopupOpen(true);
        } else {
            // Fallback default
            setIsPopupOpen(true);
        }
    };

    // Track what kind of submit is happening to prevent race conditions
    const submitActionTypeRef = useRef("save");

    const onValidationError = (errors) => {
        console.log("Form validation errors:", errors);
        if (errors?.customerName) {
            toast.error(errors.customerName.message || "Please enter Customer Name", { theme: "colored" });
        } else if (errors?.contact) {
            toast.error(errors.contact.message || "Please enter Contact Number", { theme: "colored" });
        } else {
            toast.error("Please ensure all required fields are filled correctly", { theme: "colored" });
        }
    };

    // Debug wrapper for confirm button to log and trigger submit
    const handleConfirmClick = (actionType = "print") => {
        submitActionTypeRef.current = actionType;
        console.log('Confirm button clicked; current orderProducts length:', (watchedOrderProducts || []).length);
        // call react-hook-form submit handler with validation error callback
        handleSubmit(onSubmit, onValidationError)();
    };

    // REPEAT ORDER

    const handleRepeatOrder = () => {
        if (!state) return;

        const newReceived = cumulativeReceived + currentReceivedAmt;
        const newBalance = totalAmount - newReceived;
        const paymentStatus = newBalance <= 0 ? "Completed" : "Pending";

        const newPayment = {
            totalAmount,
            receivedAmount: currentReceivedAmt,
            balanceAmount: newBalance,
            paymentDate: new Date().toISOString().split("T")[0],
            status: paymentStatus,
        };

        const updatedList = [...paymentList, newPayment];
        setPaymentList(updatedList);

        // Reset form fields
        reset({
            receivedAmount: "",
            balanceAmount: 0,
            totalInvoiceAmount: totalWithPrevious,
            transactionStatus: paymentStatus,
        });

        const currentValues = getValues();
        const repeatFormData = {
            registerId: registerId,
            customerName: currentValues?.customerName || "",
            contactNumber: currentValues?.contact || "",
            salesDate: currentValues?.salesDate || moment().format("YYYY-MM-DD"),
            gstNumber: currentValues?.gstNumber || "",
            totalAmount: Number(currentValues?.totalInvoiceAmount || 0),
            receivedAmount: Number(currentReceivedAmt || 0),
            balanceAmount: Number((currentValues?.totalInvoiceAmount || 0) - (currentReceivedAmt || 0)),
            paymentDate: currentValues?.salesDate || moment().format("YYYY-MM-DD"),
            executiveName: currentValues?.executiveName || "",
            vehicleNumber: currentValues?.vehiclenumber || "",
            transactionMode: currentValues?.transactionMode || "",
            transactionStatus: paymentStatus,
            deliveryStatus: currentValues?.deliveryStatus || "Processing",
            items: (Array.isArray(watchedOrderProducts) ? watchedOrderProducts : [])
                .filter(p => p.product && Number(p.quantity) >= 1)
                .map((p) => {
                    const quantity = Number(p.quantity || 0);
                    const unitPrice = Number(p.unitPrice || 0);
                    const gstPercentLevel = Number(p.gstPercentage || 0);
                    const subtotal = quantity * unitPrice;
                    const gstAmount = (subtotal * gstPercentLevel) / 100;
                    const rowTotalPrice = Number((subtotal + gstAmount).toFixed(2));

                    return {
                        productName: p.product || p.productName || p.productNameEnglish || "",
                        hsnCode: p.hsnCode || "",
                        quantity: quantity,
                        quantityType: p.quantityType || "",
                        unitPrice: unitPrice,
                        gstPercent: gstPercentLevel,
                        price: rowTotalPrice,
                        mrp: Number(p.mrpPrice || 0),
                    };
                }),
            discount: Number(currentValues?.discountPrice || 0)
        };

        repeatMutate(repeatFormData);
    };

    // GET API END FOR EXECUTIVE

    // API status for products
    const inventory = Array.isArray(products) ? products : [];
    const inventoryLoading = !!purchaseLoading;
    const defaultImage = "https://cdn-icons-png.flaticon.com/512/3081/3081840.png";
    const productOptions = inventory.map((product) => ({
        label: product?.productName || product?.productNameEnglish || "",
        value: product?.productName || product?.productNameEnglish || "",
    }));

    const executives = executiveData?.data;
    const executiveNames = (Array.isArray(executives) ? executives : []).map((data) => {
        return `${data?.executiveName} : ${data?.phoneNumber}`;
    });


    // Custom MenuList to append an Add Product button (same as NewPurchase)
    const CustomMenuList = (menuProps) => (
        <components.MenuList {...menuProps}>
            {menuProps.children}
            <div style={{ padding: 8, borderTop: '1px solid #eee' }}>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary w-100"
                    onClick={() => {
                        navigate('/addProduct');
                        if (menuProps?.selectProps?.onMenuClose) menuProps.selectProps.onMenuClose();
                    }}
                >
                    + Add Product
                </button>
            </div>
        </components.MenuList>
    );

    // --- UI Values (using memoized calculations) ---
    const previewReceived = currentReceivedAmt > 0 ? currentReceivedAmt : 0;
    const previewBalance = memoBalanceAmount;

    const filteredFastProducts = (inventory || []).filter(p => {
        const matchesCategory = selectedCategory === "All" || p.category === selectedCategory;
        const nameToSearch = (p.productNameEnglish || p.productName || "").toLowerCase();
        const matchesSearch = !searchTermFast || nameToSearch.includes(searchTermFast.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const handleCardClick = (product) => {
        console.log("handleCardClick called for product:", product);
        const orderList = watchedOrderProducts || [];
        const productNameKey = product.productName || product.productNameEnglish || product.product || "";

        const existingIndex = orderList.findIndex(p => p.product === productNameKey);
        if (existingIndex !== -1) {
            const currentQty = Number(orderList[existingIndex].quantity || 0);
            setValue(`orderProducts.${existingIndex}.quantity`, currentQty + 1);
            return;
        }

        // Default to "Retailer" if no level is selected yet
        const level = customersLevels || "Retailer";
        const levelPriceMap = {
            "MRP": product?.highLevelCustomerPrice || product?.highLevelCustomer,
            "Retailer": product?.midLevelCustomerPrice || product?.midLevelCustomer,
            "Wholesale": product?.lowLevelCustomerPrice || product?.lowLevelCustomer,
        };
        const updatedPrice = Number(levelPriceMap[level] || product?.highLevelCustomerPrice || product?.highLevelCustomer || 0);

        // Check if the first row is empty (unselected product). If so, update it instead of appending.
        const emptyIndex = orderList.findIndex(p => !p.product);
        const productData = {
            product: productNameKey,
            quantity: 1,
            quantityType: product.quantityType || "",
            otherQuantityType: "",
            unitPrice: updatedPrice,
            price: Number(updatedPrice * 1),
            orderAdd: "",
            gstPercentage: Number(product.gstPercentage || 0),
            mrpPrice: Number(product.mrpPrice || product.mrp || 0),
            hsnCode: product.hsncode || product.hsnCode || "",
            salesDate: moment().format("YYYY-MM-DD"),
        };

        if (emptyIndex !== -1) {
            update(emptyIndex, productData);
            console.log("updated empty row at index", emptyIndex, "with product", productNameKey);
        } else {
            append(productData);
            console.log("appended new row for product", productNameKey);
        }
    };

    const openCountModal = (index) => {
        const orderList = watchedOrderProducts || [];
        const product = orderList[index];
        setTempProduct({ ...product, index });
        setTempCount(Number(product.quantity || 1));
        setIsCountModalOpen(true);
    };

    const handleUpdateTempCount = () => {
        if (tempProduct) {
            setValue(`orderProducts.${tempProduct.index}.quantity`, tempCount);
        }
        setIsCountModalOpen(false);
    };

    const popupItems = (orderFormData?.items || []).filter(
        (it) => (it?.productName || it?.product) && Number(it?.quantity || 0) > 0
    );
    const popupTotalQuantity = popupItems.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
    // popupSubtotal should be the sum of individual item totals (which include GST)
    const popupSubtotal = popupItems.reduce((sum, it) => sum + Number(it.price || 0), 0);
    const popupRef = useRef(null);

    const [billingType, setBillingType] = useState(window.innerWidth <= 768 ? "mobile" : "direct");
    useEffect(() => {
        const handleResize = () => setBillingType(window.innerWidth <= 768 ? "mobile" : "direct");
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);








const handlePrint = async () => {
    try {
        await handlePosPrint(orderFormData, null, {
            profile: PRINTER_PROFILES.EPSON_80,
            transport: "rawbt",
            cutPaper: false,
            footerPaddingLines: 5,
            feedBeforeCutLines: 8,
        });

        if (state?.orderId || state?.id) {
            await updateApi(orderFormData);
        } else {
            await mutate(orderFormData);
        }

        setIsPopupOpen(false);
    } catch (err) {
        console.error("Print failed:", err);
        alert("Printing failed: " + (err?.message || "Unknown error"));
    }
};

    const handlePopupCancel = () => {
        // Clear the popup state and reset form to defaults (acts as Clear All)
        setIsPopupOpen(false);
        setOrderFormData(null);
        setPaymentList([]);
        reset({
            receivedAmount: "",
            balanceAmount: 0,
            totalInvoiceAmount: totalWithPrevious,
            transactionStatus: "Completed",
        });
    };

    return (
        <>
            <div className="form_main_block">
                <div className="form_sub_block">
                    {activeTab === "manual" ? (
                        <form className="p-4" onSubmit={handleSubmit(onSubmit)}>
                            {/* <div>
              <h6
                className="payment_title"
                style={{ borderBottom: "1px solid", display: "inline-block" }}
              >
                CUSTOMER DETAILS
              </h6>
            </div> */}
                            {/* ✅ Top Section */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between", // Title left, input right
                                    flexWrap: "wrap", // Makes it responsive
                                    gap: "12px",
                                    marginBottom: "15px",
                                }}
                            >
                                {/* 📝 Title */}
                                <h6
                                    className="payment_title"
                                    style={{
                                        borderBottom: "1px solid #000",
                                        margin: 0,
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    CUSTOMER DETAILS
                                </h6>

                                {/* 📝 Show Select Customer only if NOT in edit mode */}
                                {/* Customer selection disabled per request — use plain Customer Name field below */}
                            </div>

                            <div className="row px-3 d-flex gap-3 my-2">
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">
                                        Customer Name
                                        <span style={{ color: "red" }}>*</span>
                                    </label>
                                    <Controller
                                        name="customerName"
                                        control={control}
                                        defaultValue=""
                                        rules={{ required: "Customer Name is required" }}
                                        render={({ field, fieldState: { error } }) => (
                                            <>
                                                <CreatableSelect
                                                    {...field}
                                                    options={customerOptions}
                                                    placeholder="Search Customer"
                                                    isSearchable
                                                    isClearable
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        control: (base, state) => ({
                                                            ...base,
                                                            minHeight: '40px',
                                                            height: '40px',
                                                            borderRadius: '6px',
                                                            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                                                            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : null,
                                                        }),
                                                        valueContainer: (base) => ({
                                                            ...base,
                                                            padding: '4px 8px',
                                                            height: '40px',
                                                        }),
                                                        input: (base) => ({
                                                            ...base,
                                                            margin: 0,
                                                            padding: 0,
                                                        }),
                                                        placeholder: (base) => ({
                                                            ...base,
                                                            color: '#6c757d',
                                                        }),
                                                    }}
                                                    value={customerOptions.find(opt => opt.value === field.value) || (field.value ? { label: field.value, value: field.value } : null)}
                                                    onChange={(selectedOption) => {
                                                        const val = selectedOption ? selectedOption.value : "";
                                                        field.onChange(val);

                                                        if (selectedOption?.customer) {
                                                            const c = selectedOption.customer;
                                                            setValue("contact", c.contact || c.contactNumber || "");
                                                            setValue("customerLevel", c.customerLevel || "Retailer");
                                                            setValue("gstNumber", c.gstNumber || "");
                                                        }
                                                    }}
                                                    onCreateOption={(inputValue) => {
                                                        field.onChange(inputValue);
                                                        setValue("customerName", inputValue);
                                                    }}
                                                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                                                />
                                                {error && <p className="text-danger small mt-1 mb-0">{error.message}</p>}
                                            </>
                                        )}
                                    />
                                </div>
                                {/* Customer Level Select */}
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">Customer Level</label>
                                    <Controller
                                        name="customerLevel"
                                        control={control}
                                        defaultValue="Retailer"
                                        render={({ field }) => (
                                            <select {...field} className="product_input_field">
                                                <option value="MRP">MRP</option>
                                                <option value="Retailer">Retailer</option>
                                                <option value="Wholesale">Wholesale</option>
                                            </select>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">
                                        Contact Number
                                        <span style={{ color: "red" }}>*</span>
                                    </label>
                                    <Controller
                                        name="contact"
                                        control={control}
                                        defaultValue=""
                                        rules={{ required: "Mobile Number is required" }}
                                        render={({ field, fieldState: { error } }) => (
                                            <>
                                                <input
                                                    {...field}
                                                    type="tel"
                                                    placeholder="8010389345"
                                                    className="product_input_field"
                                                />
                                                {error && <p className="text-danger small mt-1 mb-0">{error.message}</p>}
                                            </>
                                        )}
                                    />
                                </div>
                                {/* Customer address, state, and pincode hidden per request (kept in state only) */}

                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">Sales Date</label>
                                    <Controller
                                        name="salesDate"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                type="date"
                                                className="product_input_field"
                                            />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">GST Number</label>
                                    <Controller
                                        name="gstNumber"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                type="text"
                                                placeholder="8010389345"
                                                className="product_input_field"
                                            />
                                        )}
                                    />
                                </div>

                                {/* Customer Level and Line Route hidden per request (kept in state only) */}

                                {false && (
                                    <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                        <label className="executive_label">Invoice No</label>
                                        <Controller
                                            name="invoiceNo"
                                            control={control}
                                            defaultValue=""
                                            render={({ field }) => (
                                                <input
                                                    {...field}
                                                    type="text"
                                                    placeholder="Invoice No"
                                                    className="product_input_field"
                                                />
                                            )}
                                        />
                                    </div>
                                )}
                                {/* Route Name input hidden per request (value still set from state/selected customer) */}
                                {state?.id ? (
                                    <>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                            <label
                                                className="executive_label"
                                                style={{ visibility: "hidden" }}
                                            >
                                                Line Route
                                            </label>

                                            <div className="repeat_order d-flex align-items-center">
                                                <div className="download_image_block d-flex align-items-center justify-content-center" style={{ width: "30px", height: "30px", fontSize: "18px" }}>
                                                    🔄
                                                </div>
                                                <button
                                                    className="download_btn"
                                                    type="button"
                                                    onClick={handleRepeatOrder}
                                                >
                                                    {repeatLoading ? (
                                                        <>
                                                            <i className="fa fa-spinner fa-spin"></i>
                                                        </>
                                                    ) : (
                                                        <> REPEAT ORDER</>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <></>
                                )}
                            </div>

                            {/* <div className="row px-3 d-flex gap-3 my-2">
       
            </div> */}


                            <div className="product-details-container mb-4">
                                <h6
                                    className="payment_title mb-3"
                                    style={{
                                        borderBottom: "1px solid #dee2e6",
                                        display: "inline-block",
                                        paddingBottom: '4px'
                                    }}
                                >
                                    PRODUCT DETAILS
                                </h6>

                                {/* Desktop Header Row */}
                                <div className="row g-2 align-items-center mb-2 fw-bold d-none d-md-flex text-dark">
                                    <div className="col-md-3">Product *</div>
                                    <div className="col-md-1">HSN</div>
                                    <div className="col-md-1">Qty *</div>
                                    <div className="col-md-2">Type</div>
                                    <div className="col-md-1">Unit Price</div>
                                    <div className="col-md-1">GST (%)</div>
                                    <div className="col-md-1">Price</div>
                                    <div className="col-md-1">MRP</div>
                                    <div className="col-md-1 text-center">Action</div>
                                </div>

                                {/* Product Rows */}

                                {(Array.isArray(orderProductFields) ? orderProductFields : []).map((field, index) => {
                                    const productValue = watch(`orderProducts.${index}.product`);
                                    const quantity = watchedOrderProducts?.[index]?.quantity || "";
                                    const unitPrice = watchedOrderProducts?.[index]?.unitPrice || "";
                                    const gstPercentage = watchedOrderProducts?.[index]?.gstPercentage || "";

                                    return (
                                        <div className="row g-2 align-items-start mb-3 mb-md-2 pb-3 pb-md-2 border-bottom border-light-subtle" key={field?.id}>

                                            {/* 1. Product Select (col-md-3) */}
                                            <div className="col-12 col-md-3">
                                                <label className="d-md-none form-label text-secondary mb-1">Product *</label>
                                                <Controller
                                                    name={`orderProducts.${index}.product`}
                                                    control={control}
                                                    rules={{ required: index === orderProductFields.length - 1 ? false : "Product is required" }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <div>
                                                            <Select
                                                                {...field}
                                                                options={productOptions}
                                                                placeholder="Select Product"
                                                                menuPortalTarget={document.body}
                                                                styles={{
                                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                                    control: (base, state) => ({
                                                                        ...base,
                                                                        minHeight: '38px',
                                                                        height: '38px',
                                                                        borderRadius: '0.375rem',
                                                                        borderColor: state.isFocused ? '#86b7fe' : '#212529',
                                                                        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : null,
                                                                    }),
                                                                    valueContainer: (base) => ({
                                                                        ...base,
                                                                        padding: '0 8px',
                                                                        height: '38px',
                                                                    }),
                                                                    indicatorsContainer: (base) => ({
                                                                        ...base,
                                                                        height: '38px',
                                                                    }),
                                                                }}
                                                                onChange={(selectedOption) => {
                                                                    const selectedValue = selectedOption?.value || "";
                                                                    field.onChange(selectedValue);
                                                                    const selectedProduct = products?.find((p) =>
                                                                        p?.productName === selectedValue ||
                                                                        p?.productNameEnglish === selectedValue ||
                                                                        p?.product === selectedValue
                                                                    );

                                                                    if (selectedProduct) {
                                                                        // Set HSN, Type, Unit Price based on selected product and customer level
                                                                        const levelPriceMap = {
                                                                            "MRP": selectedProduct?.highLevelCustomerPrice || selectedProduct?.highLevelCustomer,
                                                                            "Retailer": selectedProduct?.midLevelCustomerPrice || selectedProduct?.midLevelCustomer,
                                                                            "Wholesale": selectedProduct?.lowLevelCustomerPrice || selectedProduct?.lowLevelCustomer,
                                                                        };
                                                                        const updatedPrice = levelPriceMap[customersLevels] || selectedProduct?.highLevelCustomerPrice || selectedProduct?.highLevelCustomer || "";

                                                                        setValue(`orderProducts.${index}.unitPrice`, updatedPrice);
                                                                        setValue(`orderProducts.${index}.gstPercentage`, selectedProduct?.gstPercentage || "");
                                                                        setValue(`orderProducts.${index}.quantityType`, selectedProduct?.quantityType || "");
                                                                        setValue(`orderProducts.${index}.hsnCode`, selectedProduct?.hsncode || selectedProduct?.hsnCode || "");
                                                                    } else {
                                                                        setValue(`orderProducts.${index}.unitPrice`, "");
                                                                        setValue(`orderProducts.${index}.gstPercentage`, "");
                                                                        setValue(`orderProducts.${index}.quantityType`, "");
                                                                        setValue(`orderProducts.${index}.hsnCode`, "");
                                                                    }

                                                                    // Automatically append a new row if this is the last row and a product is selected
                                                                    if (selectedValue && index === orderProductFields.length - 1) {
                                                                        append({
                                                                            product: "",
                                                                            quantity: 1,
                                                                            quantityType: "",
                                                                            otherQuantityType: "",
                                                                            unitPrice: "",
                                                                            price: "",
                                                                            orderAdd: "",
                                                                            gstPercentage: "",
                                                                            mrpPrice: "",
                                                                            hsnCode: "",
                                                                            salesDate: moment().format("YYYY-MM-DD"),
                                                                        });
                                                                    }
                                                                }}
                                                                value={productOptions?.find((option) => option?.value === field?.value) || null}
                                                                isClearable
                                                                components={{ MenuList: CustomMenuList }}
                                                            />
                                                            {error && <div className="text-danger small mt-1" style={{ fontSize: '0.75rem' }}>{error?.message}</div>}
                                                        </div>
                                                    )}
                                                />
                                            </div>

                                            {/* 2. HSN (col-md-1) */}
                                            <div className="col-6 col-md-1">
                                                <label className="d-md-none form-label text-secondary mb-1">HSN</label>
                                                <Controller
                                                    name={`orderProducts.${index}.hsnCode`}
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            className="form-control border-dark"
                                                            placeholder="HSN"
                                                            readOnly
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* 3. Quantity (col-md-1) */}
                                            <div className="col-6 col-md-1">
                                                <label className="d-md-none form-label text-secondary mb-1">Qty *</label>
                                                <Controller
                                                    name={`orderProducts.${index}.quantity`}
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field, fieldState: { error } }) => (
                                                        <>
                                                            <input
                                                                {...field}
                                                                type="number"
                                                                step="any"
                                                                className={`form-control border-dark ${error ? "is-invalid" : ""
                                                                    }`}
                                                                placeholder="Qty"
                                                            />
                                                            {error && <div className="invalid-feedback" style={{ fontSize: '0.75rem' }}>{error.message}</div>}
                                                        </>
                                                    )}
                                                />
                                            </div>

                                            {/* 4. Type (col-md-2) */}
                                            <div className="col-6 col-md-2">
                                                <label className="d-md-none form-label text-secondary mb-1">Type</label>
                                                <Controller
                                                    name={`orderProducts.${index}.quantityType`}
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => {
                                                        const qtyOptions = (Array.isArray(quantityTypesData) ? quantityTypesData : []).map(qt => ({
                                                            id: qt.id || qt.quantityTypeId,
                                                            label: qt.name || qt.quantityTypeName,
                                                            value: qt.name || qt.quantityTypeName,
                                                            originalData: qt
                                                        }));

                                                        const selectedOption = qtyOptions.find(opt => opt.value === field.value) || null;

                                                        return (
                                                            <Select
                                                                {...field}
                                                                isLoading={quantityTypesLoading}
                                                                options={qtyOptions}
                                                                value={selectedOption}
                                                                onChange={(selected) => field.onChange(selected ? selected.value : '')}
                                                                components={{ Option: CustomOption }}
                                                                onEditItem={handleEditQuantityType}
                                                                onDeleteItem={handleDeleteQuantityType}
                                                                placeholder="Type"
                                                                classNamePrefix="react-select"
                                                                menuPortalTarget={document.body}
                                                                styles={{
                                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                                    control: (base, state) => ({
                                                                        ...base,
                                                                        minHeight: '38px',
                                                                        height: '38px',
                                                                        borderRadius: '0.375rem',
                                                                        borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
                                                                        boxShadow: state.isFocused ? '0 0 0 .25rem rgba(13,110,253,.25)' : 'none',
                                                                    }),
                                                                    valueContainer: (base) => ({
                                                                        ...base,
                                                                        padding: '0 8px',
                                                                        height: '38px',
                                                                    }),
                                                                    input: (base) => ({
                                                                        ...base,
                                                                        margin: 0,
                                                                        padding: 0,
                                                                    }),
                                                                    indicatorsContainer: (base) => ({
                                                                        ...base,
                                                                        height: '38px',
                                                                    }),
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </div>

                                            {/* 5. Unit Price (col-md-1) */}
                                            <div className="col-6 col-md-1">
                                                <label className="d-md-none form-label text-secondary mb-1">Unit Price</label>
                                                <Controller
                                                    name={`orderProducts.${index}.unitPrice`}
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="number"
                                                            step="any"
                                                            className="form-control border-dark"
                                                            placeholder="Price"
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* 6. GST (col-md-1) */}
                                            <div className="col-6 col-md-1">
                                                <label className="d-md-none form-label text-secondary mb-1">GST (%)</label>
                                                <Controller
                                                    name={`orderProducts.${index}.gstPercentage`}
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="number"
                                                            step="any"
                                                            className="form-control border-dark"
                                                            placeholder="GST"
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* 7. Price (col-md-1) */}
                                            <div className="col-6 col-md-1">
                                                <label className="d-md-none form-label text-secondary mb-1">Price</label>
                                                <Controller
                                                    name={`orderProducts.${index}.price`}
                                                    control={control}
                                                    defaultValue={0}
                                                    render={({ field }) => {
                                                        const quantity = Number(watchedOrderProducts[index]?.quantity || 0);
                                                        const unitPrice = Number(watchedOrderProducts[index]?.unitPrice || 0);
                                                        const gstPercentLevel = Number(watchedOrderProducts[index]?.gstPercentage || 0);
                                                        const computedPrice = Number(((quantity * unitPrice) + ((quantity * unitPrice * gstPercentLevel) / 100)).toFixed(2));

                                                        return (
                                                            <input
                                                                {...field}
                                                                value={computedPrice || ""}
                                                                type="number"
                                                                step="any"
                                                                className="form-control border-dark"
                                                                placeholder="Total"
                                                                readOnly
                                                            />
                                                        );
                                                    }}
                                                />
                                            </div>

                                            {/* 8. MRP (col-md-1) */}
                                            <div className="col-6 col-md-1">
                                                <label className="d-md-none form-label text-secondary mb-1">MRP</label>
                                                <Controller
                                                    name={`orderProducts.${index}.mrpPrice`}
                                                    control={control}
                                                    defaultValue={0}
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="number"
                                                            step="any"
                                                            className="form-control border-dark"
                                                            placeholder="MRP"
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* 9. Action (col-6 on mobile, col-md-1 on desktop) */}
                                            <div className="col-6 col-md-1">
                                                <label className="d-md-none form-label text-secondary mb-1 invisible">Action</label>
                                                <div className="d-flex align-items-center justify-content-center" style={{ height: '38px' }}>
                                                    {index === orderProductFields.length - 1 && (
                                                        <div
                                                            className="d-flex align-items-center justify-content-center"
                                                            style={{ width: '38px', height: '38px', cursor: 'pointer' }}
                                                            title="Add New Product"
                                                            onClick={() => {
                                                                append({
                                                                    product: "",
                                                                    quantity: 1,
                                                                    quantityType: "",
                                                                    otherQuantityType: "",
                                                                    unitPrice: "",
                                                                    discountPrice: "",
                                                                    price: "",
                                                                    orderAdd: "",
                                                                    gstPercentage: "",
                                                                    mrpPrice: "",
                                                                    hsnCode: "",
                                                                    salesDate: moment().format("YYYY-MM-DD"),
                                                                });
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: "30px",
                                                                    height: "30px",
                                                                    cursor: "pointer",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    backgroundColor: "#007bff",
                                                                    color: "white",
                                                                    borderRadius: "5px",
                                                                    fontSize: "20px"
                                                                }}
                                                            >
                                                                <span>+</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {index > 0 && (
                                                        <div
                                                            className="d-flex align-items-center justify-content-center border border-danger rounded-circle p-1"
                                                            style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                                                            title="Remove Product"
                                                            onClick={() => remove(index)}
                                                        >
                                                            <span className="text-danger fw-bold" style={{ fontSize: '1.2rem', lineHeight: 1 }}>&times;</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <hr className="my-4" style={{ border: "0.5px solid #e0e0e0" }} />

                            <div className="row px-3 d-flex gap-3 my-2">
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">GST %(Over All) </label>
                                    <Controller
                                        name="gstPercentage"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <>
                                                <input
                                                    {...field}
                                                    type="number"
                                                    step="any"
                                                    placeholder="GST"
                                                    className="product_input_field border-dark"
                                                />
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">
                                        Discount Amount (&#8377;)
                                    </label>
                                    <Controller
                                        name="discountPrice"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <>
                                                <input
                                                    {...field}
                                                    type="number"
                                                    step="any"
                                                    placeholder="Discount"
                                                    className="product_input_field border-dark"
                                                />
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">Total Amount </label>
                                    <Controller
                                        name="totalInvoiceAmount"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <>
                                                <input
                                                    {...field}
                                                    type="number"
                                                    step="any"
                                                    placeholder="0.00"
                                                    className="product_input_field border-dark"
                                                    readOnly
                                                />
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">Received Amount</label>
                                    <Controller
                                        name="receivedAmount"
                                        control={control}
                                        defaultValue=""
                                        render={({ field, fieldState: { error } }) => (
                                            <>
                                                <input
                                                    {...field}
                                                    type="number"
                                                    step="any"
                                                    placeholder="Received Amount"
                                                    className="product_input_field border-dark"
                                                />
                                                {error && (
                                                    <p className="text-danger small">{error?.message}</p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">Balance Amount</label>
                                    <Controller
                                        name="balanceAmount"
                                        control={control}
                                        defaultValue={0}
                                        render={({ field }) => (
                                            <>
                                                <input
                                                    {...field}
                                                    type="number"
                                                    step="any"
                                                    placeholder="0.00"
                                                    className="product_input_field border-dark"
                                                    readOnly
                                                />
                                            </>
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Moved Transaction and Invoice fields here */}
                            <div className="row px-3 d-flex gap-3 my-2">
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">Invoice No</label>
                                    <Controller
                                        name="invoiceNo"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                type="text"
                                                placeholder="Invoice No"
                                                className="product_input_field"
                                            />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">Transaction Mode </label>
                                    <Controller
                                        name="transactionMode"
                                        control={control}
                                        render={({ field }) => (
                                            <select {...field} className="product_input_field">
                                                <option value="">None</option>
                                                <option value="Cash">Cash</option>
                                                <option value="Online">Online</option>
                                                <option value="Credit">Credit</option>
                                            </select>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">Transaction Status </label>
                                    <Controller
                                        name="transactionStatus"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <select {...field} className="product_input_field">
                                                <option value="">None</option>
                                                <option value="Pending">Pending</option>
                                                <option value="Completed">Completed</option>
                                            </select>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                    <label className="executive_label">Delivery Status </label>
                                    <Controller
                                        name="deliveryStatus"
                                        control={control}
                                        defaultValue="Processing"
                                        render={({ field }) => (
                                            <select {...field} className="product_input_field">
                                                <option value="">None</option>
                                                <option value="Processing">Processing</option>
                                                <option value="Canceled">Canceled</option>
                                                <option value="Return">Return</option>
                                                <option value="Delivered">Delivered</option>
                                            </select>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="row px-3 d-flex gap-3 my-2">
                                <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0" style={{ display: "none" }}>
                                    {false && (
                                        <>
                                            <label className="executive_label">
                                                Executive Name
                                            </label>
                                            <Controller
                                                name="executiveName"
                                                control={control}
                                                defaultValue=""
                                                render={({ field, fieldState: { error } }) => (
                                                    <input {...field} className="product_input_field" />
                                                )}
                                            />
                                        </>
                                    )}
                                </div>
                                {/* Sales/GST/Invoice/Transaction row removed from Manual (kept in Fast Billing) */}

                                {/* <hr />
                            <h6
                                className="payment_title"
                                style={{ borderBottom: "1px solid", display: "inline-block" }}
                            >
                                Payment Details
                            </h6> */}

                                {lastOrder && !state?.id && (
                                    <>
                                        <h6
                                            className="payment_title"
                                            style={{
                                                display: "inline-block",
                                            }}
                                        >
                                            Previous Details
                                        </h6>
                                        <div className="table-responsive">
                                            <table style={{ width: "100%" }}>
                                                <thead>
                                                    <tr>
                                                        <th className="payment_table_heading">Total Amount</th>
                                                        <th className="payment_table_heading">
                                                            Received Amount
                                                        </th>
                                                        <th className="payment_table_heading">
                                                            Balance Amount
                                                        </th>
                                                        <th className="payment_table_heading">Payment Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {lastOrder &&
                                                        (Array.isArray(lastOrder?.payments) ? lastOrder.payments : []).map((entry, index) => (
                                                            <tr key={index}>
                                                                <td className="payment_table_data">
                                                                    {Number(entry?.totalAmount || 0).toFixed(2)}
                                                                </td>
                                                                <td className="payment_table_data">
                                                                    {Number(entry?.receivedAmount || 0).toFixed(2)}
                                                                </td>
                                                                <td className="payment_table_data">
                                                                    {Number(entry?.balanceAmount || 0).toFixed(2)}
                                                                </td>
                                                                <td className="payment_table_data">
                                                                    {entry?.paymentDate
                                                                        ? moment(entry?.paymentDate).format(
                                                                            "DD-MM-YYYY"
                                                                        )
                                                                        : "-"}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <hr />
                                    </>
                                )}
                                <h6
                                    className="payment_title"
                                    style={{ borderBottom: "1px solid", display: "inline-block" }}
                                >
                                    Current Payment{" "}
                                </h6>
                                <div className="table-responsive">
                                    <table style={{ width: "100%" }}>
                                        <thead>
                                            <tr>
                                                <th className="payment_table_heading">Total Amout</th>
                                                <th className="payment_table_heading">Received Amount</th>
                                                <th className="payment_table_heading">Balance Amount</th>
                                                <th className="payment_table_heading">Payment Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(Array.isArray(paymentList) ? paymentList : []).map((entry, index) => (
                                                <tr key={index}>
                                                    <td className="payment_table_data">
                                                        {Number(entry?.totalAmount || 0).toFixed(2)}
                                                    </td>
                                                    <td className="payment_table_data">
                                                        {Number(entry?.receivedAmount || 0).toFixed(2)}
                                                    </td>
                                                    <td className="payment_table_data">
                                                        {Number(entry?.balanceAmount || 0).toFixed(2)}
                                                    </td>
                                                    <td className="payment_table_data">
                                                        {entry?.paymentDate
                                                            ? moment(entry?.paymentDate).format("DD-MM-YYYY")
                                                            : "-"}
                                                    </td>
                                                </tr>
                                            ))}
                                            {totalAmount > 0 && (
                                                <tr className="table-info">
                                                    <td className="payment_table_data">
                                                        {Number(totalAmount || 0).toFixed(2)}
                                                    </td>
                                                    <td className="payment_table_data">
                                                        {previewReceived > 0 ? Number(previewReceived).toFixed(2) : ""}
                                                    </td>
                                                    <td className="payment_table_data">
                                                        {Number(previewBalance || 0).toFixed(2)}
                                                    </td>
                                                    <td className="payment_table_data">
                                                        {moment().format("DD-MM-YYYY")}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <hr />


                            </div>
                            <div className="row px-3 my-4">
                                <div className="col-12 d-flex gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-success px-4"
                                        disabled={postLoading || putLoading}
                                        onClick={() => {
                                            submitActionTypeRef.current = "save";
                                            handleSubmit(onSubmit, onValidationError)();
                                        }}
                                    >
                                        {postLoading || putLoading ? (
                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                        ) : (
                                            <>{isEditMode ? "Update Order" : "Save"}</>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary px-4"
                                        onClick={() => {
                                            submitActionTypeRef.current = "print";
                                            handleSubmit(onSubmit, onValidationError)();
                                        }}
                                    >
                                        Print
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger px-4"
                                        onClick={() => {
                                            reset({
                                                receivedAmount: "",
                                                balanceAmount: 0,
                                                totalInvoiceAmount: totalWithPrevious,
                                                transactionStatus: "Pending",
                                            });
                                            navigate("/viewOrder");
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </form>
                    ) : (
                        <div className="fast-billing-container p-4">
                            <div className="row">
                                <div className="col-12 col-md-8 border-end">
                                    <div className="mb-4">
                                        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
                                            <h5 className="mb-0">Select Products</h5>
                                            <div className="category-tabs d-flex gap-2 overflow-auto pb-1" style={{ maxWidth: '100%' }}>
                                                {categoriesList.map((cat) => (
                                                    <button
                                                        key={cat}
                                                        type="button"
                                                        className={`btn btn-sm rounded-pill px-3 ${selectedCategory === cat ? 'btn-primary' : 'btn-outline-primary'}`}
                                                        onClick={() => setSelectedCategory(cat)}
                                                    >
                                                        {cat}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="search-container" style={{ maxWidth: "350px" }}>
                                            <input
                                                type="text"
                                                className="form-control rounded-pill"
                                                placeholder="🔍 Search products..."
                                                value={searchTermFast}
                                                onChange={(e) => setSearchTermFast(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="row row-cols-1 row-cols-md-3 g-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                                        {inventoryLoading ? (
                                            <div className="w-100 d-flex flex-column align-items-center justify-content-center py-5">
                                                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="text-muted mt-3">Loading products...</p>
                                            </div>
                                        ) : Array.isArray(filteredFastProducts) && filteredFastProducts.length > 0 ? (
                                            (Array.isArray(filteredFastProducts) ? filteredFastProducts : []).map((p, idx) => (
                                                <div className="col" key={idx}>
                                                    <div
                                                        className="card h-100 product-card mt-5"
                                                        onClick={() => handleCardClick(p)}
                                                        style={{
                                                            cursor: "pointer",
                                                            borderRadius: "16px",
                                                            position: "relative",
                                                            paddingTop: "44px",
                                                            border: "1.5px solid #e0e7ff",
                                                            background: "linear-gradient(160deg, #f8f9ff 0%, #ffffff 100%)",
                                                            boxShadow: "0 4px 15px rgba(99,102,241,0.08)",
                                                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                                        }}
                                                        onMouseEnter={e => {
                                                            e.currentTarget.style.transform = "translateY(-6px)";
                                                            e.currentTarget.style.boxShadow = "0 12px 30px rgba(99,102,241,0.18)";
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.currentTarget.style.transform = "translateY(0)";
                                                            e.currentTarget.style.boxShadow = "0 4px 15px rgba(99,102,241,0.08)";
                                                        }}
                                                    >
                                                        {/* Gradient top accent bar */}
                                                        <div style={{
                                                            position: "absolute",
                                                            top: 0, left: 0, right: 0,
                                                            height: "6px",
                                                            borderRadius: "16px 16px 0 0",
                                                            background: "linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4)",
                                                        }} />

                                                        {/* Product image circle */}
                                                        <div
                                                            className="product-image-container"
                                                            style={{
                                                                position: "absolute",
                                                                top: "-40px",
                                                                left: "50%",
                                                                transform: "translateX(-50%)",
                                                                width: "80px",
                                                                height: "80px",
                                                                borderRadius: "50%",
                                                                overflow: "hidden",
                                                                border: "4px solid #fff",
                                                                boxShadow: "0 4px 14px rgba(99,102,241,0.25)",
                                                                backgroundColor: "#f0f0ff"
                                                            }}
                                                        >
                                                            <img
                                                                src={p.productImage ? `data:image/png;base64,${p.productImage}` : (p.placeholderImage || defaultImage)}
                                                                alt={p.productNameEnglish}
                                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                            />
                                                        </div>

                                                        <div className="card-body text-center pt-3 pb-3">
                                                            <h6 className="card-title fw-bold mb-1" style={{ color: "#3730a3", fontSize: "0.85rem", lineHeight: "1.3" }}>
                                                                {p.productName || p.productNameEnglish}
                                                            </h6>
                                                            {p.productName && p.productNameEnglish && (
                                                                <div className="text-muted small mb-2" style={{ fontSize: "0.75rem" }}>{p.productNameEnglish}</div>
                                                            )}
                                                            <span style={{
                                                                display: "inline-block",
                                                                background: "linear-gradient(135deg, #10b981, #059669)",
                                                                color: "#fff",
                                                                borderRadius: "20px",
                                                                padding: "3px 12px",
                                                                fontSize: "0.82rem",
                                                                fontWeight: "700",
                                                                marginTop: "4px",
                                                                letterSpacing: "0.3px"
                                                            }}>
                                                                ₹{Number(
                                                                    (customersLevels === 'Retailer'
                                                                        ? (p.midLevelCustomer || p.midLevelCustomerPrice)
                                                                        : (customersLevels === 'Wholesale'
                                                                            ? (p.lowLevelCustomer || p.lowLevelCustomerPrice)
                                                                            : (p.highLevelCustomer || p.highLevelCustomerPrice)
                                                                        )
                                                                    ) || p.midLevelCustomer || p.midLevelCustomerPrice || p.highLevelCustomer || 0
                                                                ).toFixed(2)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-12 text-center py-5">
                                                <p className="text-muted">No products found</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-4">
                                    <div className="sticky-top" style={{ top: "20px" }}>
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h5 className="mb-0">Order Summary</h5>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => replace([])}
                                                disabled={(watchedOrderProducts || []).filter(p => p.product).length === 0}
                                            >
                                                Clear All
                                            </button>
                                        </div>


                                        <div className="order-items-list mb-4" style={{ maxHeight: "40vh", overflowY: "auto" }}>
                                            {(() => {
                                                const filteredProducts = (watchedOrderProducts || [])
                                                    .map((p, originalIdx) => ({ ...p, originalIdx }))
                                                    .filter(p => p.product);

                                                return filteredProducts.length > 0 ? (
                                                    <div className="list-group list-group-flush">
                                                        {filteredProducts.map((p, idx) => (
                                                            <div className="list-group-item d-flex justify-content-between align-items-center bg-light mb-2 rounded border-0" key={idx}>
                                                                <div className="flex-grow-1">
                                                                    <div className="fw-bold tw-text-slate-800" style={{ fontSize: "0.95rem" }}>{p.product}</div>
                                                                    <div className="d-flex align-items-center gap-1 opacity-75">
                                                                        <small className="tw-text-slate-500">₹{Number(p.unitPrice || 0).toFixed(2)}</small>
                                                                        <small className="tw-text-slate-400">×</small>
                                                                        <small className="tw-text-slate-500">{p.quantity}</small>
                                                                        <small className="tw-text-slate-400">=</small>
                                                                        <small className="fw-bold tw-text-blue-600">₹{Number(p.price || 0).toFixed(2)}</small>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div className="d-flex align-items-center bg-white border rounded">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-light border-0 px-2 fw-bold"
                                                                            onClick={() => {
                                                                                const currentQty = Number(p.quantity || 1);
                                                                                if (currentQty > 1) setValue(`orderProducts.${p.originalIdx}.quantity`, currentQty - 1);
                                                                            }}
                                                                        >
                                                                            &#8722;
                                                                        </button>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm text-center border-0 fw-bold px-0 no-arrows"
                                                                            style={{ width: "35px", background: "transparent", fontSize: "0.85rem" }}
                                                                            value={p.quantity}
                                                                            onChange={(e) => {
                                                                                const val = parseInt(e.target.value);
                                                                                setValue(`orderProducts.${p.originalIdx}.quantity`, isNaN(val) ? 0 : val);
                                                                            }}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-light border-0 px-2 fw-bold"
                                                                            onClick={() => {
                                                                                setValue(`orderProducts.${p.originalIdx}.quantity`, Number(p.quantity || 0) + 1);
                                                                            }}
                                                                        >
                                                                            &#43;
                                                                        </button>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => remove(p.originalIdx)}
                                                                    >
                                                                        &times;
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-center py-4 bg-light rounded text-muted">No items selected</p>
                                                );
                                            })()}
                                        </div>

                                        <div className="card shadow-sm border-0 bg-light">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span>Subtotal</span>
                                                    <span className="fw-bold text-success">₹{(totalAmount - (state?.balanceAmount || 0) + Number(discount)).toFixed(2)}</span>
                                                </div>
                                                {Number(discount) > 0 && (
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span>Discount</span>
                                                        <span className="text-danger">-₹{Number(discount).toFixed(2)}</span>
                                                    </div>
                                                )}
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span>Previous Balance</span>
                                                    <span className="text-danger">₹{existingBalance.toFixed(2)}</span>
                                                </div>
                                                <hr />
                                                <div className="d-flex justify-content-between mb-4">
                                                    <span className="h5">Total</span>
                                                    <span className="h5 text-primary">₹{totalAmount.toFixed(2)}</span>
                                                </div>

                                                <div className="d-flex flex-column gap-2 mt-3">
                                                    <button
                                                        className="btn w-100 py-2 fw-bold"
                                                        style={{ backgroundColor: "blue", color: "white" }}
                                                        onClick={() => handleConfirmClick("print")}
                                                        disabled={postLoading || putLoading || (watchedOrderProducts || []).filter(p => p.product).length === 0}
                                                    >
                                                        {postLoading || putLoading ? (
                                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                                        ) : null}
                                                        {isEditMode ? "UPDATE CONFIRM PAYMENT" : "CONFIRM PAYMENT"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <hr className="my-4" style={{ border: "0.5px solid #e0e0e0" }} />

                            {/* Customer & Transaction details (below Select Products) - Fast Billing layout per reference */}
                            <div className="row mb-3 bg-light p-3 rounded shadow-sm mx-0">
                                <div className="col-12 col-md-3">
                                    <label className="executive_label">Customer Name<span style={{ color: 'red' }}>*</span></label>
                                    <Controller
                                        name="customerName"
                                        control={control}
                                        defaultValue=""
                                        rules={{ required: "Customer Name is required" }}
                                        render={({ field, fieldState: { error } }) => (
                                            <>
                                                <CreatableSelect
                                                    {...field}
                                                    options={customerOptions}
                                                    placeholder="Search Customer"
                                                    isSearchable
                                                    isClearable
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        control: (base, state) => ({
                                                            ...base,
                                                            minHeight: '40px',
                                                            height: '40px',
                                                            borderRadius: '6px',
                                                            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                                                            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : null,
                                                        }),
                                                        valueContainer: (base) => ({
                                                            ...base,
                                                            padding: '4px 8px',
                                                            height: '40px',
                                                        }),
                                                        input: (base) => ({
                                                            ...base,
                                                            margin: 0,
                                                            padding: 0,
                                                        }),
                                                        placeholder: (base) => ({
                                                            ...base,
                                                            color: '#6c757d',
                                                        }),
                                                    }}
                                                    value={customerOptions.find(opt => opt.value === field.value) || (field.value ? { label: field.value, value: field.value } : null)}
                                                    onChange={(selectedOption) => {
                                                        const val = selectedOption ? selectedOption.value : "";
                                                        field.onChange(val);

                                                        if (selectedOption?.customer) {
                                                            const c = selectedOption.customer;
                                                            setValue("contact", c.contact || c.contactNumber || "");
                                                            setValue("customerLevel", c.customerLevel || "Retailer");
                                                            setValue("gstNumber", c.gstNumber || "");
                                                        }
                                                    }}
                                                    onCreateOption={(inputValue) => {
                                                        field.onChange(inputValue);
                                                        setValue("customerName", inputValue);
                                                    }}
                                                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                                                />
                                                {error && <p className="text-danger small mt-1 mb-0">{error.message}</p>}
                                            </>
                                        )}
                                    />
                                </div>
                                {/* Customer Level Select for Fast Billing */}
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Customer Level</label>
                                    <Controller
                                        name="customerLevel"
                                        control={control}
                                        defaultValue="Retailer"
                                        render={({ field }) => (
                                            <select {...field} className="form-select">
                                                <option value="MRP">MRP</option>
                                                <option value="Retailer">Retailer</option>
                                                <option value="Wholesale">Wholesale</option>
                                            </select>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-3">
                                    <label className="executive_label">Contact Number<span style={{ color: 'red' }}>*</span></label>
                                    <Controller
                                        name="contact"
                                        control={control}
                                        defaultValue=""
                                        rules={{ required: "Mobile Number is required" }}
                                        render={({ field, fieldState: { error } }) => (
                                            <>
                                                <input {...field} type="tel" placeholder="8010389345" className="form-control" />
                                                {error && <p className="text-danger small mt-1 mb-0">{error.message}</p>}
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Sales Date</label>
                                    <Controller
                                        name="salesDate"
                                        control={control}
                                        defaultValue={moment().format('YYYY-MM-DD')}
                                        render={({ field }) => (
                                            <input {...field} type="date" className="form-control" />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">GST Number</label>
                                    <Controller
                                        name="gstNumber"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <input {...field} type="text" placeholder="GST" className="form-control" />
                                        )}
                                    />
                                </div>
                                {false && (
                                    <div className="col-12 col-md-2">
                                        <label className="executive_label">Invoice No</label>
                                        <Controller
                                            name="invoiceNo"
                                            control={control}
                                            defaultValue=""
                                            render={({ field }) => (
                                                <input {...field} type="text" placeholder="Invoice No" className="form-control" />
                                            )}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="row mb-3 bg-light p-3 rounded shadow-sm mx-0">
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">GST %(Over All)</label>
                                    <Controller
                                        name="gstPercentage"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <input {...field} type="number" step="any" placeholder="GST" className="form-control" />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Discount Amount (₹)</label>
                                    <Controller
                                        name="discountPrice"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <input {...field} type="number" step="any" placeholder="Discount" className="form-control" />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Total Amount</label>
                                    <Controller
                                        name="totalInvoiceAmount"
                                        control={control}
                                        defaultValue={0}
                                        render={({ field }) => (
                                            <input {...field} type="number" step="any" placeholder="0" className="form-control" readOnly />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Received Amount</label>
                                    <Controller
                                        name="receivedAmount"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <input {...field} type="number" step="any" placeholder="Pay Amount" className="form-control" />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Balance Amount</label>
                                    <Controller
                                        name="balanceAmount"
                                        control={control}
                                        defaultValue={0}
                                        render={({ field }) => (
                                            <input {...field} type="number" step="any" placeholder="0" className="form-control" readOnly />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="row mb-3 bg-light p-3 rounded shadow-sm mx-0">
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Invoice No</label>
                                    <Controller
                                        name="invoiceNo"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <input {...field} type="text" placeholder="Invoice No" className="form-control" />
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Transaction Mode</label>
                                    <Controller
                                        name="transactionMode"
                                        control={control}
                                        render={({ field }) => (
                                            <select {...field} className="form-select">
                                                <option value="">None</option>
                                                <option value="Cash">Cash</option>
                                                <option value="Online">Online</option>
                                                <option value="Credit">Credit</option>
                                            </select>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Transaction Status</label>
                                    <Controller
                                        name="transactionStatus"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <select {...field} className="form-select">
                                                <option value="">None</option>
                                                <option value="Pending">Pending</option>
                                                <option value="Completed">Completed</option>
                                            </select>
                                        )}
                                    />
                                </div>
                                <div className="col-12 col-md-2">
                                    <label className="executive_label">Delivery Status</label>
                                    <Controller
                                        name="deliveryStatus"
                                        control={control}
                                        defaultValue="Processing"
                                        render={({ field }) => (
                                            <select {...field} className="form-select">
                                                <option value="Processing">Processing</option>
                                                <option value="Canceled">Canceled</option>
                                                <option value="Return">Return</option>
                                                <option value="Delivered">Delivered</option>
                                            </select>
                                        )}
                                    />
                                </div>
                                {/* Hidden fields in Fast Billing (kept in state) */}
                                {false && (
                                    <>
                                        <Controller name="executiveName" control={control} render={({ field }) => <input {...field} />} />
                                        <Controller name="vehiclenumber" control={control} render={({ field }) => <input {...field} />} />
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Payment confirmation popup (lightweight, minimal custom CSS) */}
            {
                isPopupOpen && (
                    <div
                        className="modal fade show popup-overlay d-block"
                        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100vh', background: 'rgba(0,0,0,0.55)', zIndex: 2147483647 }}
                        data-bs-backdrop="static"
                        tabIndex="-1"
                    >
                        <div className="modal-dialog modal-dialog-centered modal-md">
                            <div className="popup modal-content p-4" ref={popupRef}>
                                <div className="text-center mb-3 border-bottom pb-2">
                                    <h5 className="fw-bold mb-1">New Vinayaga Snacks & Confectioneries</h5>
                                    <p className="small mb-0">No:3/132 Pillaiyar Koil Street, Sathankuppam,</p>
                                    <p className="small mb-0">Kelambakkam - 603103, Tamilnadu</p>
                                    <p className="small mb-0 fw-bold">Mobile : 9074420538</p>
                                </div>

                                <div className="d-flex justify-content-between mb-3" style={{ fontSize: "12px", gap: "20px" }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: "flex", alignItems: "flex-start" }}>
                                            <span style={{ width: "70px", flexShrink: 0, fontWeight: "bold" }}>Customer</span>
                                            <span style={{ width: "12px", flexShrink: 0 }}>:</span>
                                            <span style={{ fontWeight: "600", wordBreak: "break-all" }}>{orderFormData?.customerName || "-"}</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
                                            <span style={{ width: "70px", flexShrink: 0, fontWeight: "bold" }}>Mobile</span>
                                            <span style={{ width: "12px", flexShrink: 0 }}>:</span>
                                            <span>{orderFormData?.contactNumber || "-"}</span>
                                        </div>
                                        {orderFormData?.invoiceNo && (
                                            <div style={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
                                                <span style={{ width: "70px", flexShrink: 0, fontWeight: "bold" }}>Invoice</span>
                                                <span style={{ width: "12px", flexShrink: 0 }}>:</span>
                                                <span>#{orderFormData.invoiceNo}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ flexShrink: 0 }}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <span style={{ width: "45px", flexShrink: 0, fontWeight: "bold" }}>Date</span>
                                            <span style={{ width: "12px", flexShrink: 0 }}>:</span>
                                            <span style={{ minWidth: "80px" }}>{moment(orderFormData?.salesDate).format("DD-MM-YYYY")}</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
                                            <span style={{ width: "45px", flexShrink: 0, fontWeight: "bold" }}>Mode</span>
                                            <span style={{ width: "12px", flexShrink: 0 }}>:</span>
                                            <span style={{ fontWeight: "600", minWidth: "80px" }}>{orderFormData?.transactionMode || "-"}</span>
                                        </div>
                                    </div>
                                </div>

                                <h6 className="pop-up-order-content mt-2">
                                    {isEditMode ? "Edit Order / Update Payment" : "Order Details"}
                                </h6>
                                <div className="table-responsive">
                                    <table style={{ width: "100%" }}>
                                        <thead>
                                            <tr>
                                                <th className="popup-table-head">Item Name</th>
                                                <th className="popup-table-head">Quantity</th>
                                                <th className="popup-table-head">Price</th>
                                                <th className="popup-table-head">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(Array.isArray(popupItems) ? popupItems : []).map((data, index) => {
                                                const itemTotal = data?.price ? Number(data?.price || 0) : (Number(data?.quantity || 0) * Number(data?.unitPrice || 0));
                                                return (
                                                    <tr key={index}>
                                                        <td className="popup-table-data">{data?.productName || "-"}</td>
                                                        <td className="popup-table-data">{data?.quantity || 0}</td>
                                                        <td className="popup-table-data">{data?.unitPrice || 0}</td>
                                                        <td className="popup-table-data">{itemTotal || 0}</td>
                                                    </tr>
                                                );
                                            })}
                                            <tr>
                                                <td className="popup-table-data" style={{ padding: "4px", borderBottom: "1px dashed #ccc", borderTop: "2px dashed black" }}>Total Items</td>
                                                <td className="popup-table-data" style={{ padding: "4px", borderBottom: "1px dashed #ccc", borderTop: "2px dashed black" }}>{popupTotalQuantity || 0}</td>
                                                <td className="popup-table-data" style={{ padding: "4px", borderBottom: "1px dashed #ccc", borderTop: "2px dashed black" }}></td>
                                                <td className="popup-table-data" style={{ padding: "4px", borderBottom: "1px dashed #ccc", borderTop: "2px dashed black" }}></td>
                                            </tr>
                                            {/* Subtotal row */}
                                            <tr>
                                                <td colSpan="3" className="popup-table-data text-end" style={{ padding: "4px", fontWeight: "bold" }}>Subtotal</td>
                                                <td className="popup-table-data" style={{ padding: "4px", fontWeight: "bold" }}>{Number(popupSubtotal || 0).toFixed(2)}</td>
                                            </tr>
                                            {/* Discount row with fallback */}
                                            {(() => {
                                                const explicitDiscount = Number(orderFormData?.discount || 0);
                                                const totalAmount = Number(orderFormData?.totalAmount || 0);
                                                const calculatedDiscount = popupSubtotal > totalAmount ? (popupSubtotal - totalAmount) : 0;
                                                const finalDiscount = explicitDiscount > 0 ? explicitDiscount : calculatedDiscount;

                                                if (finalDiscount > 0) {
                                                    return (
                                                        <tr>
                                                            <td colSpan="3" className="popup-table-data text-end" style={{ padding: "4px", borderBottom: "2px dashed black" }}>Discount</td>
                                                            <td className="popup-table-data text-danger" style={{ padding: "4px", borderBottom: "2px dashed black" }}>- {Number(finalDiscount).toFixed(2)}</td>
                                                        </tr>
                                                    );
                                                }
                                                return (
                                                    <tr>
                                                        <td colSpan="4" style={{ borderBottom: "1px dashed #ccc" }}></td>
                                                    </tr>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="invoice-total">
                                    <span>Total</span>
                                    <span>{Number(orderFormData?.totalAmount || 0).toFixed(2)}</span>
                                </p>
                                <div style={{ marginTop: "20px", textAlign: "center", fontSize: "12px", fontWeight: "600" }}>
                                    Goods Once Sold cannot be taken back<br />
                                    Thank You !!! Visit Again !!!
                                </div>
                                <div className="d-flex justify-content-center mt-5 gap-2 align-items-center">
                                    <button type="button" className="invoice-print-button" disabled={postLoading || putLoading} onClick={() => {
                                        if (isEditMode) {
                                            updateApi(orderFormData);
                                        } else {
                                            mutate(orderFormData);
                                        }
                                    }}>
                                        {postLoading || putLoading ? (<span className="spinner-border spinner-border-sm me-1" role="status"></span>) : (<> {isEditMode ? "UPDATE ORDER" : "SAVE"}</>)}
                                    </button>
                                    <button type="button" className="invoice-print-button" onClick={handlePrint}>
                                        PRINT
                                    </button>
                                    <button type="button" className="invoice-print-button" onClick={() => { handlePopupCancel(); }}>
                                        CANCEL
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Bootstrap 5 Modal for Quantity */}
            {
                isCountModalOpen && (
                    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow">
                                <div className="modal-header border-0 pb-0">
                                    <h5 className="modal-title fw-bold">Update Quantity</h5>
                                    <button type="button" className="btn-close" onClick={() => setIsCountModalOpen(false)}></button>
                                </div>
                                <div className="modal-body text-center py-4">
                                    <p className="text-muted mb-4">{tempProduct?.product}</p>
                                    <div className="d-flex justify-content-center align-items-center gap-4">
                                        <button
                                            className="btn btn-outline-primary rounded-circle"
                                            style={{ width: "45px", height: "45px" }}
                                            onClick={() => setTempCount(prev => Math.max(0, prev - 1))}
                                        >
                                            <i className="fa fa-minus"></i>
                                        </button>
                                        <input
                                            type="number"
                                            className="form-control text-center fw-bold"
                                            style={{ width: "80px", fontSize: "1.2rem" }}
                                            value={tempCount}
                                            onChange={(e) => setTempCount(Number(e.target.value))}
                                        />
                                        <button
                                            className="btn btn-outline-primary rounded-circle"
                                            style={{ width: "45px", height: "45px" }}
                                            onClick={() => setTempCount(prev => prev + 1)}
                                        >
                                            <i className="fa fa-plus"></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 pt-0 pb-4 justify-content-center gap-3">
                                    <button type="button" className="btn btn-light px-4" onClick={() => setIsCountModalOpen(false)}>Cancel</button>
                                    <button type="button" className="btn btn-primary px-4" onClick={handleUpdateTempCount}>Update</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
            <CreateQuantityType editData={editingQuantityType} onClose={() => setEditingQuantityType(null)} />
        </>
    );
};

export default NewSales;
